/**
 * ChatMessagesView - 消息列表视图组件
 * 从 ChatPage.tsx 提取的消息渲染逻辑
 */

import React from 'react';
import { Message, Conversation, MessageStatus } from '@/types';
import TreeMessageRenderer from '@/components/TreeMessageRenderer';
import MessageBubble from '@/components/MessageBubble';

export interface ChatMessagesViewProps {
  displayConversation: Conversation;
  isCurrentGroupChat: boolean;
  useMessagesArray?: boolean; // ð¥ skill ä¼è¯ï¼ä½¿ç¨ messages æ°ç»æ¸²æï¼WebSocket æ´æ° messagesï¼ä¸æ´æ° treeDataï¼
  isReadOnlyMode: boolean;
  canChangeVersionInThisView: boolean;
  isSharedRecordView: boolean;
  selectedAgent?: { id: string; name: string; logo?: string } | null;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;

  // Callbacks
  handleSourceClick: (sourceId: string, page?: number) => void;
  handleDelete: (message: Message) => void;
  handleRetry: (message: Message) => Promise<void>;
  handleEditSave: (
    message: Message,
    newContent: string,
    fileIds?: string[]
  ) => Promise<void>;
  handleChangeChoiceForTree: (depth: number, newVersionIndex: number) => void;
  handleChangeChoiceForSharedTree: (
    depth: number,
    newVersionIndex: number
  ) => void;
  handleChangeChoice: (messageId: string, newIndex: number) => void;
  handleChangeChoiceForSharedMessages: (
    messageId: string,
    newIndex: number
  ) => void;
  handleStopRequest: () => void;
  // 🔥 新增：取消消息的回调（传递消息信息用于调用取消 API）
  handleCancelMessage?: (
    messageId: number | string | undefined,
    previousMessageId: number | string | undefined,
    messageStatus: MessageStatus | undefined,
    conversationId: string
  ) => Promise<{ success: boolean }> | void;
  setReplyToMessage?: (message: Message | null) => void;
  replyToMessage?: Message | null;
}

export function ChatMessagesView({
  displayConversation,
  isCurrentGroupChat,
  useMessagesArray = false,
  isReadOnlyMode,
  canChangeVersionInThisView,
  isSharedRecordView,
  selectedAgent,
  scrollContainerRef,
  messagesEndRef,
  handleSourceClick,
  handleDelete,
  handleRetry,
  handleEditSave,
  handleChangeChoiceForTree,
  handleChangeChoiceForSharedTree,
  handleChangeChoice,
  handleChangeChoiceForSharedMessages,
  handleStopRequest: _handleStopRequest,
  handleCancelMessage,
  setReplyToMessage,
}: ChatMessagesViewProps) {
  // 检查 treeData 中是否有正在流式输出或等待中的节点
  const isAnyStreaming =
    displayConversation.treeData?.some((node: any) => {
      const checkStreamingOrPending = (n: any): boolean => {
        if (n.isStreaming) return true;
        if (n.messageStatus === 'waiting' || n.messageStatus === 'ongoing')
          return true;
        if (n.children) return n.children.some(checkStreamingOrPending);
        return false;
      };
      return checkStreamingOrPending(node);
    }) || false;

  // 对于没有 treeData 的对话，动态调整 assistant 消息的版本显示
  const getMessagesWithAdjustedVersions = () => {
    const messages: Message[] = (displayConversation.messages ||
      []) as Message[];

    return messages.map((message: Message, index: number) => {
      if (message.role !== 'assistant') {
        return message;
      }

      // 找到前面最近的 user 消息
      let userMessage = null;
      for (let i = index - 1; i >= 0; i--) {
        if (messages[i].role === 'user') {
          userMessage = messages[i];
          break;
        }
      }

      if (!userMessage) {
        return message;
      }

      const userChoicesCount = userMessage.choices?.length || 1;
      const assistantChoicesCount = message.choices?.length || 1;

      // 只有当 user 有多个版本时，才认为版本是成对关联的
      if (userChoicesCount > 1) {
        const userActiveChoice = userMessage.activeChoice ?? 0;
        const newMultiVersion = Math.min(
          userActiveChoice + 1,
          assistantChoicesCount
        );
        const currentActiveChoice = message.activeChoice ?? 0;
        const adjustedActiveChoice = Math.min(
          currentActiveChoice,
          newMultiVersion - 1
        );

        const adjustedMessage = {
          ...message,
          activeChoice: adjustedActiveChoice,
          multiVersion: newMultiVersion,
          // 🔥 显式保留 isCanceled 属性
          isCanceled: message.isCanceled,
        };
        return adjustedMessage;
      }

      return message;
    });
  };

  return (
    <div
      ref={scrollContainerRef}
      className="flex-1 overflow-y-auto min-h-0 scroll-container relative"
    >
      <div className="max-w-4xl mx-auto py-4 md-container px-4 sm:px-6 break-all whitespace-normal">
        {/* 如果有 treeData 且不为空，使用递归渲染；否则使用旧的 map 渲染 */}
        {/* 群聊不使用 treeData，直接使用 messages 数组 */}
        {displayConversation.treeData &&
        displayConversation.treeData.length > 0 &&
        !isCurrentGroupChat &&
        !useMessagesArray ? (
          <TreeMessageRenderer
            treeData={displayConversation.treeData}
            versionPath={displayConversation.versionPath || {}}
            conversationId={displayConversation.conversationId}
            onSourceClick={handleSourceClick}
            onDelete={isReadOnlyMode ? undefined : handleDelete}
            onRetry={isReadOnlyMode ? undefined : handleRetry}
            isAnyStreaming={isAnyStreaming}
            onChangeChoice={
              canChangeVersionInThisView
                ? isSharedRecordView
                  ? handleChangeChoiceForSharedTree
                  : handleChangeChoiceForTree
                : undefined
            }
            onEditSave={isReadOnlyMode ? undefined : handleEditSave}
            selectedAgent={selectedAgent || undefined}
            readOnly={isReadOnlyMode}
            onCancel={handleCancelMessage}
          />
        ) : (
          // 使用 MessageBubble 渲染
          (() => {
            const messagesWithAdjustedVersions =
              getMessagesWithAdjustedVersions();
            const isAnyMessageStreaming = messagesWithAdjustedVersions.some(
              (m: Message) => m.isStreaming
            );

            return messagesWithAdjustedVersions.map((message: Message) => (
              <MessageBubble
                key={message.id}
                message={message}
                onSourceClick={handleSourceClick}
                onDelete={
                  isReadOnlyMode ? undefined : () => handleDelete(message)
                }
                onRetry={
                  isReadOnlyMode ? undefined : () => handleRetry(message)
                }
                onChangeChoice={
                  canChangeVersionInThisView
                    ? isSharedRecordView
                      ? handleChangeChoiceForSharedMessages
                      : handleChangeChoice
                    : undefined
                }
                onEditSave={isReadOnlyMode ? undefined : handleEditSave}
                selectedAgent={selectedAgent || undefined}
                conversationId={displayConversation?.conversationId}
                readOnly={isReadOnlyMode}
                isAnyStreaming={isAnyMessageStreaming}
                isGroupChat={isCurrentGroupChat}
                onReply={isCurrentGroupChat ? setReplyToMessage : undefined}
              />
            ));
          })()
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}

export default ChatMessagesView;
